import type {
	API_AcceptInvitationResponse,
	API_InviteStaffMemberRequest,
	API_InviteStaffMemberResponse,
	API_ListOrganizationInvitationsResponse,
	API_ListOrganizationStaffResponse,
	API_ListUnifiedStaffResponse,
	API_OrganizationStaffMember,
	API_RemoveStaffMemberResponse,
	API_RevokeInvitationResponse,
	API_UpdateStaffMemberRequest,
	API_UpdateStaffMemberRoleRequest,
	API_UpdateStaffMemberRoleResponse,
	API_ValidateInvitationResponse,
	PaginationRequest,
} from "@pilatestime/shared-types";
import {
	queryOptions,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

// API functions
export const fetchOrganizationStaff = (
	token: string,
	organizationId: string,
): Promise<API_ListOrganizationStaffResponse> =>
	api
		.get(`/organizations/${organizationId}/staff`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const fetchStaffMemberById = (
	token: string,
	organizationId: string,
	staffId: string,
): Promise<API_ListOrganizationStaffResponse[0]> =>
	api
		.get(`/organizations/${organizationId}/staff/${staffId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const fetchUnifiedStaffAndInvitations = (
	token: string,
	organizationId: string,
	pagination?: PaginationRequest,
	searchTerm?: string,
): Promise<API_ListUnifiedStaffResponse> => {
	const params = new URLSearchParams();

	if (pagination?.page) {
		params.append("page", pagination.page.toString());
	}
	if (pagination?.limit) {
		params.append("limit", pagination.limit.toString());
	}
	if (searchTerm?.trim()) {
		params.append("search", searchTerm.trim());
	}

	const queryString = params.toString();
	const url = `/organizations/${organizationId}/staff-and-invitations${
		queryString ? `?${queryString}` : ""
	}`;

	return api
		.get(url, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);
};

export const inviteStaffMember = (
	token: string,
	organizationId: string,
	data: API_InviteStaffMemberRequest,
): Promise<API_InviteStaffMemberResponse> =>
	api
		.post(`/organizations/${organizationId}/staff/invite`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const updateStaffMember = (
	token: string,
	organizationId: string,
	staffId: string,
	data: API_UpdateStaffMemberRequest,
): Promise<API_OrganizationStaffMember> =>
	api
		.put(`/organizations/${organizationId}/staff/${staffId}`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const updateStaffMemberRole = (
	token: string,
	organizationId: string,
	staffUserId: string,
	data: API_UpdateStaffMemberRoleRequest,
): Promise<API_UpdateStaffMemberRoleResponse> =>
	api
		.put(`/organizations/${organizationId}/staff/${staffUserId}/role`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const removeStaffMember = (
	token: string,
	organizationId: string,
	staffUserId: string,
): Promise<API_RemoveStaffMemberResponse> =>
	api
		.delete(`/organizations/${organizationId}/staff/${staffUserId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const fetchOrganizationInvitations = (
	token: string,
	organizationId: string,
	options?: {
		status?: "PENDING" | "ACCEPTED" | "EXPIRED" | "REVOKED";
		limit?: number;
		offset?: number;
	},
): Promise<API_ListOrganizationInvitationsResponse> => {
	const params = new URLSearchParams();
	if (options?.status) params.append("status", options.status);
	if (options?.limit) params.append("limit", options.limit.toString());
	if (options?.offset) params.append("offset", options.offset.toString());

	const queryString = params.toString();
	const url = `/organizations/${organizationId}/invitations${queryString ? `?${queryString}` : ""}`;

	return api
		.get(url, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);
};

export const revokeInvitation = (
	token: string,
	organizationId: string,
	invitationId: string,
): Promise<API_RevokeInvitationResponse> =>
	api
		.delete(`/organizations/${organizationId}/invitations/${invitationId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const validateInvitation = (
	token: string,
	authToken: string,
): Promise<API_ValidateInvitationResponse> =>
	api
		.get(`/invitations/validate/${token}`, {
			headers: {
				Authorization: `Bearer ${authToken}`,
			},
		})
		.then((res) => res.data);

export const acceptInvitation = (
	invitationToken: string,
	authToken: string,
): Promise<API_AcceptInvitationResponse> =>
	api
		.post(
			`/invitations/accept/${invitationToken}`,
			{},
			{
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
			},
		)
		.then((res) => res.data);

// Query options
export const fetchOrganizationStaffQueryOptions = (
	token: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["organization_staff", organizationId],
		queryFn: () => fetchOrganizationStaff(token, organizationId),
		staleTime: 2 * 60 * 1000, // 2 minutes
	});

export const fetchStaffMemberByIdQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	staffId: string,
) =>
	queryOptions({
		queryKey: ["staff_member", organizationId, staffId, userId],
		queryFn: () => fetchStaffMemberById(token, organizationId, staffId),
		staleTime: 2 * 60 * 1000, // 2 minutes
	});

export const fetchUnifiedStaffAndInvitationsQueryOptions = (
	token: string,
	organizationId: string,
	pagination?: PaginationRequest,
	searchTerm?: string,
) =>
	queryOptions({
		queryKey: [
			"unified_staff_and_invitations",
			organizationId,
			pagination,
			searchTerm,
		],
		queryFn: () =>
			fetchUnifiedStaffAndInvitations(
				token,
				organizationId,
				pagination,
				searchTerm,
			),
		staleTime: 1 * 60 * 1000, // 1 minute
	});

export const fetchOrganizationInvitationsQueryOptions = (
	token: string,
	organizationId: string,
	options?: {
		status?: "PENDING" | "ACCEPTED" | "EXPIRED" | "REVOKED";
		limit?: number;
		offset?: number;
	},
) =>
	queryOptions({
		queryKey: ["organization_invitations", organizationId, options],
		queryFn: () => fetchOrganizationInvitations(token, organizationId, options),
		staleTime: 1 * 60 * 1000, // 1 minute (shorter than staff since invitations change more frequently)
	});

export const validateInvitationQueryOptions = (
	token: string,
	authToken: string,
) =>
	queryOptions({
		queryKey: ["validate_invitation", token],
		queryFn: () => validateInvitation(token, authToken),
		staleTime: 0, // Don't cache validation results
		retry: false, // Don't retry failed validations
	});

// Mutation hooks
export const useInviteStaffMember = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			data,
		}: {
			token: string;
			organizationId: string;
			data: API_InviteStaffMemberRequest;
		}) => inviteStaffMember(token, organizationId, data),
		toastMessages: {
			loading: t("staff.toasts.inviting"),
			success: t("staff.toasts.inviteSuccess"),
			error: t("staff.toasts.inviteError"),
		},
		onSuccess: (_, variables) => {
			// Invalidate and refetch organization staff and invitations
			queryClient.invalidateQueries({
				queryKey: ["organization_staff", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["organization_invitations", variables.organizationId],
			});
			// Invalidate and refetch unified staff and invitations
			queryClient.invalidateQueries({
				queryKey: ["unified_staff_and_invitations", variables.organizationId],
			});
		},
	});
};

export const useUpdateStaffMember = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			staffId,
			data,
		}: {
			token: string;
			organizationId: string;
			staffId: string;
			data: API_UpdateStaffMemberRequest;
		}) => updateStaffMember(token, organizationId, staffId, data),
		toastMessages: {
			loading: t("staff.toasts.updating"),
			success: t("staff.form.sections.information.updateSuccess"),
			error: t("staff.form.sections.information.updateError"),
		},
		onSuccess: (_, variables) => {
			// Invalidate and refetch organization staff
			queryClient.invalidateQueries({
				queryKey: ["organization_staff", variables.organizationId],
			});
			// Invalidate and refetch unified staff and invitations
			queryClient.invalidateQueries({
				queryKey: ["unified_staff_and_invitations", variables.organizationId],
			});
			// Invalidate all staff_member queries for this staff member (across all orgs and users)
			queryClient.invalidateQueries({
				queryKey: ["staff_member"],
				predicate: (query) => {
					const [, , staffId] = query.queryKey as [
						string,
						string,
						string,
						string,
					];
					return staffId === variables.staffId;
				},
			});
		},
	});
};

export const useUpdateStaffMemberRole = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			staffUserId,
			data,
		}: {
			token: string;
			organizationId: string;
			staffUserId: string;
			data: API_UpdateStaffMemberRoleRequest;
		}) => updateStaffMemberRole(token, organizationId, staffUserId, data),
		toastMessages: {
			loading: t("staff.toasts.updating"),
			success: t("staff.toasts.updateSuccess"),
			error: t("staff.toasts.updateError"),
		},
		onSuccess: (_, variables) => {
			// Invalidate and refetch organization staff
			queryClient.invalidateQueries({
				queryKey: ["organization_staff", variables.organizationId],
			});
			// Invalidate and refetch unified staff and invitations
			queryClient.invalidateQueries({
				queryKey: ["unified_staff_and_invitations", variables.organizationId],
			});

			// Invalidate all staff_member queries for this staff member (across all orgs and users)
			queryClient.invalidateQueries({
				queryKey: ["staff_member"],
				predicate: (query) => {
					const [, , staffId] = query.queryKey as [
						string,
						string,
						string,
						string,
					];
					return staffId === variables.staffUserId;
				},
			});

			// Also invalidate user permissions since roles might have changed
			queryClient.invalidateQueries({
				queryKey: ["user_permissions"],
			});
		},
	});
};

export const useRemoveStaffMember = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			staffUserId,
		}: {
			token: string;
			organizationId: string;
			staffUserId: string;
		}) => removeStaffMember(token, organizationId, staffUserId),
		toastMessages: {
			loading: t("staff.toasts.removing"),
			success: t("staff.toasts.removeSuccess"),
			error: t("staff.toasts.removeError"),
		},
		onSuccess: (_, variables) => {
			// Invalidate and refetch organization staff
			queryClient.invalidateQueries({
				queryKey: ["organization_staff", variables.organizationId],
			});
			// Invalidate and refetch unified staff and invitations
			queryClient.invalidateQueries({
				queryKey: ["unified_staff_and_invitations", variables.organizationId],
			});

			// Also invalidate user permissions since staff was removed
			queryClient.invalidateQueries({
				queryKey: ["user_permissions"],
			});
		},
	});
};

export const useRevokeInvitation = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			invitationId,
		}: {
			token: string;
			organizationId: string;
			invitationId: string;
		}) => revokeInvitation(token, organizationId, invitationId),
		toastMessages: {
			loading: t("staff.toasts.revoking"),
			success: t("staff.toasts.revokeSuccess"),
			error: t("staff.toasts.revokeError"),
		},
		onSuccess: (_, variables) => {
			// Invalidate and refetch organization invitations
			queryClient.invalidateQueries({
				queryKey: ["organization_invitations", variables.organizationId],
			});
			// Invalidate and refetch unified staff and invitations
			queryClient.invalidateQueries({
				queryKey: ["unified_staff_and_invitations", variables.organizationId],
			});
		},
	});
};

export const useAcceptInvitation = () => {
	return useMutation({
		mutationFn: ({
			invitationToken,
			authToken,
		}: {
			invitationToken: string;
			authToken: string;
		}) => acceptInvitation(invitationToken, authToken),
	});
};
